import React from "react";
import { useTranslation } from "react-i18next";
import { BadgeChip } from "../../../../components/OrgShell/BadgeChip";
import { FieldGroup } from "../../../../components/OrgShell/FieldGroup";
import { EmailPreferenceForm } from "../email-preference-editor.component";

interface NotificationsSectionProps {
  form: EmailPreferenceForm;
  onChange: (form: EmailPreferenceForm) => void;
  variant: "vendor" | "customer";
}

/**
 * Honest presentation of the five emailPreference flags:
 * - "New order placed"            → EmailPreferenceNewOrder
 * - "Order needs your approval"   → EmailPreferencePendingOrder
 * - "Design & logo approval requests" → EmailPreferenceNewDesign AND
 *   EmailPreferenceNewLogos written together as ONE toggle
 * - "Order shipped"               → EmailPreferenceOrder, rendered disabled
 *   (COMING LATER) but still round-tripped unchanged on save
 * All five fields keep flowing through the existing profile save body.
 */
export function NotificationsSection({
  form,
  onChange,
  variant,
}: NotificationsSectionProps) {
  const { t } = useTranslation();
  const approvalRequestsChecked =
    form.emailPreferenceNewDesign || form.emailPreferenceNewLogos;

  return (
    <div className="space-y-8">
      <FieldGroup
        title={t("myProfileCore.emailNotificationsTitle")}
        description={
          variant === "vendor"
            ? t("myProfileCore.emailNotificationsDescriptionVendor")
            : t("myProfileCore.emailNotificationsDescriptionCustomer")
        }
      >
        <div className="divide-y rounded-md border">
          {variant === "vendor" && (
            <>
              <NotificationRow
                title={t("myProfileCore.newOrderPlacedTitle")}
                description={t("myProfileCore.newOrderPlacedDescription")}
                badge={
                  <BadgeChip tone="green">
                    {t("myProfileCore.badgeNew")}
                  </BadgeChip>
                }
                checked={form.emailPreferenceNewOrder}
                onChange={(next) =>
                  onChange({ ...form, emailPreferenceNewOrder: next })
                }
              />
              <NotificationRow
                title={t("myProfileCore.orderNeedsApprovalTitle")}
                description={t("myProfileCore.orderNeedsApprovalDescription")}
                checked={form.emailPreferencePendingOrder}
                onChange={(next) =>
                  onChange({ ...form, emailPreferencePendingOrder: next })
                }
              />
            </>
          )}
          <NotificationRow
            title={
              variant === "vendor"
                ? t("myProfileCore.approvalRequestsTitleVendor")
                : t("myProfileCore.approvalRequestsTitleCustomer")
            }
            description={
              variant === "vendor"
                ? t("myProfileCore.approvalRequestsDescriptionVendor")
                : t("myProfileCore.approvalRequestsDescriptionCustomer")
            }
            checked={approvalRequestsChecked}
            onChange={(next) =>
              onChange({
                ...form,
                emailPreferenceNewDesign: next,
                emailPreferenceNewLogos: next,
              })
            }
          />
          <NotificationRow
            title={t("myProfileCore.orderShippedTitle")}
            description={t("myProfileCore.orderShippedDescription")}
            badge={
              <BadgeChip tone="gray">
                {t("myProfileCore.badgeComingLater")}
              </BadgeChip>
            }
            checked={form.emailPreferenceOrder}
            disabled
          />
        </div>
      </FieldGroup>
    </div>
  );
}

function NotificationRow({
  title,
  description,
  badge,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description: string;
  badge?: React.ReactNode;
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 p-4 ${
        disabled ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
      }`}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {badge}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">
          {description}
        </span>
      </span>
    </label>
  );
}
